/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from 'react'
import {useTranslation} from 'react-i18next'
import clsx from 'clsx'
import {PlusIcon} from '@heroicons/react/24/solid'
import {PencilSquareIcon} from '@heroicons/react/24/outline'
import LeadBasicForm from 'components/leads/forms/LeadBasicForm'
import EditLeadModalNotesList from './EditLeadModalNotesList'
import EditLeadModalTasksList from './EditLeadModalTasksList'
import UiFormInner from 'components/Forms/UIFormInner'
import {UiTable, UiTableBody, UiTableContainer, UiTableHead, UiTableHeaderCell} from 'components/Ui/UiTable'
import UiButton, {UiButtonSize, UiButtonStyle, UiButtonType} from 'components/Ui/UiButton'
import type {Lead} from 'types/entities/Lead'
import type {LeadNote} from 'types/entities/LeadNote'
import type {LeadTask} from 'types/entities/LeadTask'
import {LeadStatus} from 'types/enums/LeadStatus'
import {useRejectedReasons} from 'utils/hooks/useRejectedReasons'

interface EditLeadDetailTabProps {
    lead: Lead
    selectedLead: Lead | null | undefined
    isOpportunity: boolean
    hasCompany: boolean
    hasContactPerson: boolean
    notes: LeadNote[]
    tasks: LeadTask[]
    onSuccess: (_lead?: Lead, close?: boolean) => void
    onAddNote: () => void
    onEditNote: (note: LeadNote) => void
    onDeleteNote: (note: LeadNote) => void
    onAddTask: () => void
    onEditTask: (task: LeadTask) => void
    onDeleteTask: (task: LeadTask) => void
    onCompleteTask: (task: LeadTask) => void
    onEditRejectionDetails: () => void
}

const EditLeadDetailTab: React.FC<EditLeadDetailTabProps> = ({
    lead,
    isOpportunity,
    hasCompany,
    hasContactPerson,
    notes,
    tasks,
    onSuccess,
    onAddNote,
    onEditNote,
    onDeleteNote,
    onAddTask,
    onEditTask,
    onDeleteTask,
    onCompleteTask,
    selectedLead,
    onEditRejectionDetails,
}) => {
    const {t} = useTranslation()
    const {reasons} = useRejectedReasons()
    const isCreateOfferEnabled = hasCompany && hasContactPerson
    const leadRejectionReasons = selectedLead?.extra?.rejection_reasons ?? []
    const rejectionReasonLabels = leadRejectionReasons.map((reasonId) => {
        const label = reasons.find((reason) => reason.id === reasonId)?.label || reasonId
        return {id: reasonId, label}
    })
    const rejectionOtherComment = selectedLead?.extra?.rejection_other_comment?.trim() || ''
    const rejectionComment = selectedLead?.extra?.rejection_comment?.trim() || ''
    const isRejectedOpportunity =
        isOpportunity &&
        (selectedLead?.status === LeadStatus.Rejection || selectedLead?.status === LeadStatus.Disqualified)

    return (
        <div className={clsx('EditLeadDetailTab col-span-12 grid grid-cols-12 gap-4 items-stretch')}>
            <div className={clsx('col-span-6 flex flex-col pt-4 min-w-0 h-full min-h-0')}>
                <LeadBasicForm
                    compact={false}
                    onSuccess={onSuccess}
                    lead={lead}
                    highlightMissingFields={!isCreateOfferEnabled}
                    missingCompany={!hasCompany}
                    missingContact={!hasContactPerson}
                />
            </div>
            <div className={clsx('col-span-6 flex flex-col gap-4 pt-4 min-w-0 min-h-0')}>
                {isRejectedOpportunity && (
                    <UiFormInner
                        title={t('Reasons for Rejection (Multiple Selections Possible)')}
                        actions={
                            <UiButton
                                onClick={onEditRejectionDetails}
                                style={UiButtonStyle.Secondary}
                                size={UiButtonSize.Small}
                                type={UiButtonType.Button}
                                icon={<PencilSquareIcon className={clsx('w-4 h-4')} />}
                            >
                                {t('Edit')}
                            </UiButton>
                        }
                    >
                        <div className="flex flex-col gap-2 text-sm">
                            <ul className="list-disc ml-5">
                                {rejectionReasonLabels.length > 0 ? (
                                    rejectionReasonLabels.map((reason) => <li key={reason.id}>{reason.label}</li>)
                                ) : (
                                    <li>-</li>
                                )}
                            </ul>
                            {rejectionOtherComment && (
                                <div>
                                    <span className="font-semibold">{t('Other reason')}:</span> {rejectionOtherComment}
                                </div>
                            )}
                            {rejectionComment && (
                                <div>
                                    <span className="font-semibold">{t('Optional comment')}:</span> {rejectionComment}
                                </div>
                            )}
                        </div>
                    </UiFormInner>
                )}

                <div className={clsx('min-w-0 flex-1 min-h-50')}>
                    <UiFormInner
                        className={clsx('h-full')}
                        title={isOpportunity ? t('Opportunity Notes') : t('Lead Notes')}
                        actions={
                            <UiButton
                                onClick={onAddNote}
                                style={UiButtonStyle.Primary}
                                size={UiButtonSize.Small}
                                type={UiButtonType.Button}
                                className="p-0! h-8 w-8"
                                icon={<PlusIcon className={clsx('w-5 h-5')} />}
                            />
                        }
                    >
                        <UiTableContainer className={clsx('h-full overflow-y-auto')}>
                            <UiTable className="text-xs">
                                <UiTableHead>
                                    <UiTableHeaderCell align="left">{t('Type')}</UiTableHeaderCell>
                                    <UiTableHeaderCell align="right" colSpan={2}>
                                        {t('Actions')}
                                    </UiTableHeaderCell>
                                </UiTableHead>
                                <UiTableBody>
                                    <EditLeadModalNotesList
                                        notes={notes}
                                        onEdit={onEditNote}
                                        onDelete={onDeleteNote}
                                    />
                                </UiTableBody>
                            </UiTable>
                        </UiTableContainer>
                    </UiFormInner>
                </div>

                <div className={clsx('min-w-0 flex-1 min-h-50')}>
                    <UiFormInner
                        className={clsx('h-full')}
                        title={t('Tasks')}
                        actions={
                            <UiButton
                                onClick={onAddTask}
                                style={UiButtonStyle.Primary}
                                size={UiButtonSize.Small}
                                type={UiButtonType.Button}
                                className="p-0! h-8 w-8"
                                icon={<PlusIcon className={clsx('w-5 h-5')} />}
                            />
                        }
                    >
                        <UiTableContainer className={clsx('h-full overflow-y-auto')}>
                            <UiTable className="text-xs">
                                <UiTableHead>
                                    <UiTableHeaderCell align="left">{t('Type')}</UiTableHeaderCell>
                                    <UiTableHeaderCell align="left">{t('Status')}</UiTableHeaderCell>
                                    <UiTableHeaderCell align="left">{t('Title')}</UiTableHeaderCell>
                                    <UiTableHeaderCell align="left">{t('Due Date')}</UiTableHeaderCell>
                                    <UiTableHeaderCell align="left">{t('Description')}</UiTableHeaderCell>
                                    <UiTableHeaderCell align="left">{t('Completed')}</UiTableHeaderCell>
                                    <UiTableHeaderCell align="right">{t('Actions')}</UiTableHeaderCell>
                                </UiTableHead>
                                <UiTableBody>
                                    <EditLeadModalTasksList
                                        tasks={tasks}
                                        onEdit={onEditTask}
                                        onDelete={onDeleteTask}
                                        onComplete={onCompleteTask}
                                    />
                                </UiTableBody>
                            </UiTable>
                        </UiTableContainer>
                    </UiFormInner>
                </div>
            </div>
        </div>
    )
}

export default EditLeadDetailTab
